import { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, ChevronDown, Phone, Mail, MapPin,
  Linkedin, Twitter, Facebook, Instagram, Youtube, ArrowRight
} from 'lucide-react';

function PublicLayout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  
  // État pour tous les paramètres
  const [settings, setSettings] = useState({
    site_name: 'WealthTech Pro',
    site_tagline: '',
    logo_url: '/logo.jpeg',
    favicon_url: '',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    social_linkedin: '',
    social_twitter: '',
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    footer_description: 'Expert en solutions WealthTech et migration ISO 20022 pour les institutions financières africaines.',
    footer_text: ''
  });

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Nos Services', hasDropdown: true },
    { path: '/nos-applications', label: 'Nos Applications' },
    { path: '/a-propos', label: 'À propos' },
    { path: '/temoignages', label: 'Témoignages' },
    { path: '/contact', label: 'Contact' },
  ];

  const serviceLinks = [
    { path: '/services/audit-conseil', label: 'Audit & Conseil' },
    { path: '/services/core-banking', label: 'Core Banking' },
    { path: '/services/conformite', label: 'Conformité' },
    { path: '/services/formation', label: 'Formation' },
    { path: '/services/conseil-iso-20022', label: 'Conseil ISO 20022' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    fetchSettings();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  const fetchSettings = async () => {
  try {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const res = await fetch(`${API_URL}/settings`);
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const result = await res.json();
    console.log("Paramètres récupérés:", result);

    if (result.settings) {
      setSettings(prev => ({
        ...prev,
        ...result.settings
      }));

      // Mettre à jour le favicon dynamiquement
      if (result.settings.favicon_url) {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = result.settings.favicon_url;
        } else {
          const newFavicon = document.createElement('link');
          newFavicon.rel = 'icon';
          newFavicon.href = result.settings.favicon_url;
          document.head.appendChild(newFavicon);
        }
      }

      // Mettre à jour le titre de la page dynamiquement
      if (result.settings.site_name) {
        document.title = result.settings.meta_title || result.settings.site_name;
      }

      // Mettre à jour la meta description
      if (result.settings.meta_description) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.content = result.settings.meta_description;
        } else {
          metaDesc = document.createElement('meta');
          metaDesc.name = 'description';
          metaDesc.content = result.settings.meta_description;
          document.head.appendChild(metaDesc);
        }
      }
    }
  } catch (err) {
    console.error('Erreur lors de la récupération des paramètres:', err);
  }
};
  // Vérifier si un réseau social est configuré
  const hasSocialLinks = settings.social_linkedin || settings.social_twitter || 
                         settings.social_facebook || settings.social_instagram || 
                         settings.social_youtube;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-soft' 
            : 'bg-white'
        }`}
      >
        <div className="container-custom">
          <nav className="flex items-center justify-between h-20">
            {/* Logo Dynamique */}
            <Link to="/" className="flex items-center flex-shrink-0">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.site_name || 'Logo'}
                  className="h-24 w-auto object-contain"
                />
              ) : (
                <span className="text-2xl font-bold text-primary-600">
                  {settings.site_name || 'WealthTech Pro'}
                </span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div key={link.path} className="relative">
                  {link.hasDropdown ? (
                    <div
                      className="relative"
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <NavLink
                        to={link.path}
                        className={({ isActive }) =>
                          `px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-1 ${
                            isActive 
                              ? 'text-primary-600' 
                              : 'text-dark-600 hover:text-primary-600 hover:bg-primary-50'
                          }`
                        }
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                      </NavLink>

                      <AnimatePresence>
                        {isServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-dark-100 py-2 z-50"
                          >
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.path}
                                to={service.path}
                                className="block px-4 py-2 text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                              >
                                {service.label}
                              </Link>
                            ))}
                            <div className="border-t border-dark-100 mt-2 pt-2">
                              <Link
                                to="/services"
                                className="block px-4 py-2 text-primary-600 font-medium hover:bg-primary-50 transition-colors"
                              >
                                Voir tous les services
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        `px-4 py-2 rounded-lg font-medium transition-colors ${
                          isActive 
                            ? 'text-primary-600' 
                            : 'text-dark-600 hover:text-primary-600 hover:bg-primary-50'
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/admin"
                className="text-dark-500 hover:text-primary-600 font-medium transition-colors"
              >
                Admin
              </Link>
              <Link to="/contact" className="btn-primary">
                Demander un devis
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-dark-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-dark-700" />
              ) : (
                <Menu className="w-6 h-6 text-dark-700" />
              )}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-dark-100 overflow-hidden"
            >
              <div className="container-custom py-4 space-y-2">
                {navLinks.map((link) => (
                  <div key={link.path}>
                    {link.hasDropdown ? (
                      <>
                        <button
                          onClick={() => setIsServicesOpen(!isServicesOpen)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-dark-700 hover:bg-dark-50"
                        >
                          {link.label}
                          <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isServicesOpen && (
                          <div className="ml-4 space-y-1">
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.path}
                                to={service.path}
                                className="block px-4 py-2 text-dark-600 hover:text-primary-600 rounded-lg"
                              >
                                {service.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <NavLink
                        to={link.path}
                        className={({ isActive }) =>
                          `block px-4 py-3 rounded-lg font-medium ${
                            isActive 
                              ? 'text-primary-600 bg-primary-50' 
                              : 'text-dark-700 hover:bg-dark-50'
                          }`
                        }
                      >
                        {link.label}
                      </NavLink>
                    )}
                  </div>
                ))}
                <div className="pt-4 border-t border-dark-100 space-y-2">
                  <Link
                    to="/admin"
                    className="block px-4 py-3 text-dark-600 hover:bg-dark-50 rounded-lg"
                  >
                    Admin
                  </Link>
                  <Link to="/contact" className="btn-primary w-full justify-center">
                    Demander un devis
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-dark-900 text-white">
        {/* Main Footer */}
        <div className="container-custom py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand - Logo Dynamique */}
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center flex-shrink-0">
                {settings.logo_url ? (
                  <img
                    src={settings.logo_url}
                    alt={settings.site_name || 'Logo'}
                    className="h-20 w-auto object-contain"
                  />
                ) : (
                  <span className="text-2xl font-bold text-white">
                    {settings.site_name || 'WealthTech Pro'}
                  </span>
                )}
              </Link>
              <br />
              <p className="text-dark-400 mb-6">
                {settings.footer_description || 'Expert en solutions WealthTech et migration ISO 20022 pour les institutions financières africaines.'}
              </p>
              
              {/* Réseaux sociaux dynamiques */}
              <div className="flex gap-3">
                {settings.social_linkedin && (
                  <a 
                    href={settings.social_linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center hover:bg-[#0077b5] transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {settings.social_twitter && (
                  <a 
                    href={settings.social_twitter} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center hover:bg-[#1da1f2] transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {settings.social_facebook && (
                  <a 
                    href={settings.social_facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center hover:bg-[#1877f2] transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {settings.social_instagram && (
                  <a 
                    href={settings.social_instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center hover:bg-[#e4405f] transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {settings.social_youtube && (
                  <a 
                    href={settings.social_youtube} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center hover:bg-[#ff0000] transition-colors"
                  >
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
                {/* Si aucun réseau social configuré, afficher des placeholders */}
                {!hasSocialLinks && (
                  <>
                    <span className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center">
                      <Linkedin className="w-5 h-5 text-dark-500" />
                    </span>
                    <span className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center">
                      <Twitter className="w-5 h-5 text-dark-500" />
                    </span>
                    <span className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center">
                      <Facebook className="w-5 h-5 text-dark-500" />
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Services</h4>
              <ul className="space-y-3">
                <li><Link to="/services/conseil-iso-20022" className="text-dark-400 hover:text-white transition-colors">Conseil ISO 20022</Link></li>
                <li><Link to="/services/solutions-wealthtech" className="text-dark-400 hover:text-white transition-colors">Solutions WealthTech</Link></li>
                <li><Link to="/services/core-banking" className="text-dark-400 hover:text-white transition-colors">Core Banking</Link></li>
                <li><Link to="/services/conformite" className="text-dark-400 hover:text-white transition-colors">Conformité</Link></li>
                <li><Link to="/services/formation" className="text-dark-400 hover:text-white transition-colors">Formation</Link></li>
              </ul>
            </div>

            {/* Liens */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Liens utiles</h4>
              <ul className="space-y-3">
                <li><Link to="/a-propos" className="text-dark-400 hover:text-white transition-colors">À propos</Link></li>
                <li><Link to="/nos-applications" className="text-dark-400 hover:text-white transition-colors">Nos Applications</Link></li>
                <li><Link to="/temoignages" className="text-dark-400 hover:text-white transition-colors">Témoignages</Link></li>
                <li><Link to="/blog" className="text-dark-400 hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/contact" className="text-dark-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Contact Dynamique */}
            <div>
              <h4 className="font-semibold text-lg mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  <span className="text-dark-400">
                    {settings.contact_address || 'Adresse non configurée'}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  {settings.contact_phone ? (
                    <a 
                      href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`} 
                      className="text-dark-400 hover:text-white transition-colors"
                    >
                      {settings.contact_phone}
                    </a>
                  ) : (
                    <span className="text-dark-500">Non configuré</span>
                  )}
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  {settings.contact_email ? (
                    <a 
                      href={`mailto:${settings.contact_email}`} 
                      className="text-dark-400 hover:text-white transition-colors"
                    >
                      {settings.contact_email}
                    </a>
                  ) : (
                    <span className="text-dark-500">Non configuré</span>
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright Dynamique */}
        <div className="border-t border-dark-800">
          <div className="container-custom py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-dark-500 text-sm">
                {settings.footer_text || `© ${new Date().getFullYear()} ${settings.site_name || 'WealthTech Pro'}. Tous droits réservés.`}
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="#" className="text-dark-500 hover:text-white transition-colors">
                  Mentions légales
                </Link>
                <Link to="#" className="text-dark-500 hover:text-white transition-colors">
                  Politique de confidentialité
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicLayout;